import React, { useEffect, useRef } from "react";

// The read-aloud menu under the header's speaker button (WEB-7123): on or off, the whole answer
// or a short summary, and the voice. Artemis's voices come first where the server has them.

const PREVIEW = "Here's how I'll read your answers."

const browserLabel = v => v.name.replace(/\s*\((Natural|Premium|Enhanced)\)/i, ' ($1)')

const VoiceMenu = ({ speaker, onClose }) => {
  const ref = useRef(null)
  useEffect(() => {
    speaker.loadVoices()
    const onKey = e => { if (e.key === 'Escape') onClose() }
    const onDown = e => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.artemis-voice-button')) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [onClose])

  const { server: serverVoices, serverDefault, browser: browserVoices } = speaker.voices
  // A remembered Artemis voice while the server can't speak reads in this browser's best voice.
  const chosen = speaker.voice.startsWith('polly:') && !serverVoices.length ? 'auto' : speaker.voice
  const pick = value => {
    speaker.setVoice(value)
    if (!speaker.enabled) speaker.setEnabled(true)
    // After the voice is set, so the sample is in it.
    setTimeout(() => speaker.preview(PREVIEW), 0)
  }

  return (
    <div className="artemis-voice-menu" role="dialog" aria-label="Read aloud" ref={ref}>
      <button type="button" role="switch" aria-checked={speaker.enabled} className="artemis-switch-row"
        onClick={() => speaker.setEnabled(!speaker.enabled)}>
        <span className="artemis-flex">Read replies aloud</span>
        <span className={`artemis-switch ${speaker.enabled ? 'artemis-switch-on' : ''}`} aria-hidden="true"><span /></span>
      </button>

      <span className="artemis-eyebrow">Read</span>
      <div className="artemis-segmented" role="radiogroup" aria-label="What to read">
        {[['full', 'The whole answer'], ['summary', 'A short summary']].map(([value, label]) => (
          <button key={value} type="button" role="radio" aria-checked={speaker.mode === value}
            className={`artemis-segment ${speaker.mode === value ? 'artemis-segment-on' : ''}`} onClick={() => speaker.setMode(value)}>
            {label}
          </button>
        ))}
      </div>

      <label className="artemis-eyebrow" htmlFor="artemis-voice-select">Voice</label>
      <select id="artemis-voice-select" className="artemis-input-field" value={chosen} onChange={e => pick(e.target.value)}>
        <option value="auto">{serverVoices.length ? `Automatic (Artemis · ${serverDefault || serverVoices[0].Name})` : 'Automatic (best available)'}</option>
        {serverVoices.length > 0 && (
          <optgroup label="Artemis voices">
            {serverVoices.map(v => <option key={v.Id} value={`polly:${v.Id}`}>{v.Name}{v.Gender ? ` · ${String(v.Gender).toLowerCase()}` : ''}</option>)}
          </optgroup>
        )}
        {browserVoices.length > 0 && (
          <optgroup label="This browser">
            {browserVoices.map(v => <option key={v.voiceURI} value={`browser:${v.voiceURI}`}>{browserLabel(v)}</option>)}
          </optgroup>
        )}
      </select>
      <span className="artemis-popover-note">
        {speaker.server ? 'Artemis reads progress as it works, then the answer.' : "Artemis's voice isn't available right now, so this browser's voice reads instead."}
      </span>
    </div>
  )
}

export default VoiceMenu
